import React, { useState, useContext, useEffect, useCallback } from 'react';
import style from './MealPlans.module.css';
import FormInput from '../../components/FormInput/FormInput';
import MealPlan from '../../components/MealPlan/MealPlan';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import UpgradePrompt from '../../components/UpgradePrompt/UpgradePrompt';
import { generateMeals, saveMealPlan } from '../../utils/generateMeals';
import { normalizeMealPlan } from '../../utils/mealPlan';
import { useAuthUser } from '../../hooks/useAuthUser';
import { checkSubscription } from '../../utils/checkSubscription';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import spinner from '../../assets/Spinner.svg';
import { toast } from 'react-toastify';
import {
	MdRestaurant,
	MdArrowBack,
	MdAutoAwesome,
} from 'react-icons/md';

const MealPlans = () => {
	const { dbUser, isDbUserResolved } = useContext(UserContext);
	const { user } = useAuthUser();
	const [mealPlans, setMealPlans] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [userInput, setUserInput] = useState('');
	const navigate = useNavigate();

	useEffect(() => {
		if (!user) {
			navigate(`/login?redirectTo=${encodeURIComponent('/meals')}`, { replace: true });
		}
	}, [user, navigate]);

	const buildFitnessContext = useCallback(() => {
		if (!dbUser) return '';

		const parts = [];
		const goal = dbUser?.interests?.goal;
		const level = dbUser?.interests?.level;
		const trainingDays = dbUser?.interests?.trainingDaysPerWeek;
		const routines = dbUser?.stryds?.routines || [];
		const activeRoutines = routines.filter(r => r.status !== 'draft');
		const recentWorkouts = (dbUser?.workoutLog || []).slice(-5);

		if (!goal && !level && activeRoutines.length === 0 && recentWorkouts.length === 0) return '';

		if (goal) parts.push(`Fitness goal: ${goal}`);
		if (level) parts.push(`Experience level: ${level}`);
		if (trainingDays) parts.push(`Training days per week: ${trainingDays}`);

		if (activeRoutines.length > 0) {
			parts.push('Active training routines:');
			activeRoutines.slice(0, 3).forEach(r => {
				const workoutTypes = (r.workouts || []).map(w => w.workoutType).join(', ');
				parts.push(`  - ${r.routineName} (${r.estimatedTimeMinutes || '?'} min): ${workoutTypes || 'various exercises'}`);
			});
		}

		if (recentWorkouts.length > 0) {
			parts.push('Recent workouts completed:');
			recentWorkouts.forEach(w => {
				parts.push(`  - ${w.routineName} (${w.durationMinutes || '?'} min)`);
			});
		}

		return `\n<fitness_context>\n${parts.join('\n')}\n</fitness_context>`;
	}, [dbUser]);

	const buildPrompt = useCallback(() => {
		const fitnessContext = buildFitnessContext();

		return `You are a professional nutritionist creating a personalized 7-day meal plan.

<user_request>
${userInput}
</user_request>
${fitnessContext}
Create a meal plan that:
1. Directly addresses the user's stated dietary preferences, restrictions, and goals.
2. Provides balanced macronutrients across each day (proteins, carbs, healthy fats).
3. Ensures variety — no meal should repeat within the same week.
4. Uses realistic, specific dish names (not generic labels like "Protein Bowl").
5. Considers cultural context when the user mentions a cuisine style.
6. Keeps meals practical and achievable for home cooking.
7. If fitness context is provided, tailor calorie targets and macros to support the user's training — higher protein and carbs on training days, adequate recovery nutrition after intense workouts, and appropriate total calories for their fitness goal.

Respond with ONLY a valid JSON object in this exact structure:

{
  "planName": "Short descriptive name for this plan",
  "reason": "2-3 sentences explaining why this plan fits the user's request and what nutritional benefits it provides.",
  "days": [
    {
      "Breakfast": { "name": "Specific dish name", "calories": 420 },
      "Lunch": { "name": "Specific dish name", "calories": 620 },
      "Dinner": { "name": "Specific dish name", "calories": 560 }
    },
    {
      "Breakfast": { "name": "Specific dish name", "calories": 430 },
      "Lunch": { "name": "Specific dish name", "calories": 610 },
      "Dinner": { "name": "Specific dish name", "calories": 550 }
    },
    {
      "Breakfast": { "name": "Specific dish name", "calories": 440 },
      "Lunch": { "name": "Specific dish name", "calories": 600 },
      "Dinner": { "name": "Specific dish name", "calories": 570 }
    },
    {
      "Breakfast": { "name": "Specific dish name", "calories": 450 },
      "Lunch": { "name": "Specific dish name", "calories": 630 },
      "Dinner": { "name": "Specific dish name", "calories": 540 }
    },
    {
      "Breakfast": { "name": "Specific dish name", "calories": 410 },
      "Lunch": { "name": "Specific dish name", "calories": 620 },
      "Dinner": { "name": "Specific dish name", "calories": 560 }
    },
    {
      "Breakfast": { "name": "Specific dish name", "calories": 435 },
      "Lunch": { "name": "Specific dish name", "calories": 615 },
      "Dinner": { "name": "Specific dish name", "calories": 545 }
    },
    {
      "Breakfast": { "name": "Specific dish name", "calories": 425 },
      "Lunch": { "name": "Specific dish name", "calories": 605 },
      "Dinner": { "name": "Specific dish name", "calories": 555 }
    }
  ]
}

The "days" array must contain exactly 7 objects, one per day. Each object has exactly three keys: "Breakfast", "Lunch", "Dinner". Each meal object must include "name" and "calories" where calories is a numeric estimate for that meal only. Output ONLY the JSON — no markdown fences, no explanation.`;
	}, [userInput, buildFitnessContext]);

	const parseMealResponse = useCallback((text) => {
		if (!text || typeof text !== 'string') throw new Error('Empty response');
		const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
		const candidate = (fenceMatch ? fenceMatch[1] : text).trim();
		try { return JSON.parse(candidate); } catch (_) {
			const start = candidate.indexOf('{');
			const end = candidate.lastIndexOf('}');
			if (start !== -1 && end !== -1 && end > start) {
				return JSON.parse(candidate.slice(start, end + 1));
			}
			throw new Error('Failed to parse meal plan JSON');
		}
	}, []);

	const handleMealInput = event => {
		setUserInput(event.target.value);
	};

	const handleGenerateMealPlans = () => {
		if (!userInput.trim()) {
			toast.info('Please describe the type of meal plan you want.');
			return;
		}
		setIsLoading(true);
		setMealPlans({});
		generateMeals(buildPrompt())
			.then((meal) => {
				const parsed = parseMealResponse(meal);
				setMealPlans(normalizeMealPlan(parsed));
			})
			.catch(err => {
				console.error('Failed to generate meal plan', err);
				toast.error('Failed to generate meal plan. Please try again.');
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleSaveMealPlan = async () => {
		if (!user || !user.id) return;
		if (!mealPlans || !mealPlans.planName || !Array.isArray(mealPlans.days)) return;
		setIsSaving(true);
		try {
			await saveMealPlan(user.id, mealPlans);
			toast.success('Meal plan saved!');
			navigate('/profile-page');
		} catch (error) {
			console.error('Failed to save meal plan', error);
			toast.error('Failed to save. Please try again.');
			setIsSaving(false);
		}
	};

	if (user && !isDbUserResolved) {
		return (
			<div className={style.LoadingState}>
				<img src={spinner} alt="" className={style.Spinner} />
				<span>Loading...</span>
			</div>
		);
	}

	if (!checkSubscription(dbUser)) {
		return (
			<UpgradePrompt
				heading="Meal Plans is a premium feature"
				description="Upgrade to access personalized AI-powered meal plans."
				onUpgrade={() => navigate('/upgrade')}
			/>
		);
	}

	return (
		<div className={style.MealPlansPage}>
			<div className={style.Header}>
				<button className={style.BackButton} onClick={() => navigate(-1)}>
					<MdArrowBack />
					<span>Back</span>
				</button>
				<div className={style.HeaderContent}>
					<div className={style.MealBadge}>
						<MdRestaurant />
						<span>AI-Powered</span>
					</div>
					<h1 className={style.PageTitle}>Meal Plans</h1>
					<p className={style.PageDescription}>
						Describe what you're looking for and we'll generate a personalized 7-day meal plan tailored to your training.
					</p>
				</div>
			</div>

			<div className={style.InputSection}>
				<div className={style.InputCard}>
					<label className={style.InputLabel}>
						<MdAutoAwesome className={style.InputIcon} />
						What type of meal plan do you want?
					</label>
					<div className={style.InputRow}>
						<FormInput
							HeadingText=""
							width="100%"
							showIsEmpty={true}
							change={handleMealInput}
							placeholder="e.g. High-protein vegetarian plan, easy Nigerian meals on a budget, Mediterranean diet for weight loss..."
							onEnter={handleGenerateMealPlans}
						/>
					</div>
					<div className={style.InputActions}>
						<PrimaryButton
							text={isLoading ? 'Generating...' : 'Generate Plan'}
							click={handleGenerateMealPlans}
							loading={isLoading}
							disabled={isLoading || !userInput.trim()}
						/>
					</div>
				</div>
			</div>

			<div className={style.ResultsSection}>
				{isLoading ? (
					<div className={style.GeneratingState}>
						<img src={spinner} alt="" className={style.GeneratingSpinner} />
						<h3 className={style.GeneratingTitle}>Crafting your meal plan</h3>
						<p className={style.GeneratingText}>
							Building a balanced 7-day plan based on your preferences and training...
						</p>
					</div>
				) : mealPlans.days && mealPlans.days.length > 0 ? (
					<div className={style.PlanResults}>
						{(mealPlans.planName || mealPlans.reason) && (
							<div className={style.PlanHeader}>
								{mealPlans.planName && (
									<h2 className={style.PlanName}>{mealPlans.planName}</h2>
								)}
								{mealPlans.reason && (
									<p className={style.PlanReason}>{mealPlans.reason}</p>
								)}
								<div className={style.PlanMeta}>
									<span className={style.PlanStat}>7 days</span>
									<span className={style.PlanStatDot}>·</span>
									<span className={style.PlanStat}>3 meals/day</span>
									<span className={style.PlanStatDot}>·</span>
									<span className={style.PlanStat}>21 meals total</span>
								</div>
							</div>
						)}

						<div className={style.MealGrid}>
							{mealPlans.days.map((day, index) => (
								<MealPlan
									key={index}
									dayNumber={index + 1}
									dayName={`Day ${index + 1}`}
									meals={day}
								/>
							))}
						</div>

						<div className={style.PlanActions}>
							<SecondaryButton
								text="Regenerate"
								click={handleGenerateMealPlans}
								disabled={isLoading}
							/>
							<PrimaryButton
								text={isSaving ? 'Saving...' : 'Save Plan'}
								click={handleSaveMealPlan}
								loading={isSaving}
							/>
						</div>
					</div>
				) : (
					<div className={style.EmptyState}>
						<MdRestaurant className={style.EmptyIcon} />
						<p className={style.EmptyText}>
							Your personalized meal plan will appear here after generation.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default MealPlans;
